// React
import { useContext } from "react"

// Components
import { ActionButton, AddProject, BaseTable, ErrorText, Loading, TableHeader, TaxCode } from "components"

// Contexts
import { DataJournalContext, DataJournalContextProvider } from "pages/Finance/Transaction/contexts"

// Form
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"


// Types
import { type FormType, getAmount } from "pages/Finance/Transaction/utils"

// Utils
import { convertNumber, useToggle } from "utils"

import { DataJournal } from "../../components/widgets/FormSection/components/AccountTable/components"

export function TransactionTable(): JSX.Element {
  // Form
  const { formState } = useFormContext<FormType>()

  return (
    <>
      <DataJournalContextProvider>
        <div className="overflow-x-auto">
          <BaseTable>
            <TableHeader>
              <tr>
                <th>-</th>
                <th>ACC INCOME</th>
                <th>MEMO</th>
                <th>AMOUNT</th>

                <th className="whitespace-nowrap">
                  PROJECT ID <AddDataProject />
                </th>

                <th>REF NO</th>
                <AddDataTaxCode />
                <th>ACTION</th>
              </tr>
            </TableHeader>

            <Body />
          </BaseTable>
        </div>
      </DataJournalContextProvider>

      <ErrorMessage
        errors={formState.errors}
        name="datajournal"
        render={({ message }) => <ErrorText text={message} />}
      />
    </>
  )
}

function AddDataProject(): JSX.Element {
  // Hooks
  const { project } = useContext(DataJournalContext)

  return <AddProject onSuccess={project.refetch} />
}

function AddDataTaxCode(): JSX.Element {
  // Hooks
  const { tax_code } = useContext(DataJournalContext)

  return <TaxCode onSuccess={tax_code.refetch} />
}

function Body(): JSX.Element {
  // Hooks
  const { project, tax_code } = useContext(DataJournalContext)

  // Form
  const { control } = useFormContext<FormType>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: "datajournal"
  })

  if (project.isLoading || tax_code.isLoading) {
    return <Loading loading />
  } else {
    return (
      <tbody>
        {fields.map((field, key) => <EditJournal key={field.id} index={key} onDeleteItem={() => remove(key)} />)}
        <DataJournal key={fields.length} method="add" onAddJournal={append} />
      </tbody>
    )
  }
}

function EditJournal(props: {
  index: number
  onDeleteItem: () => void
}): JSX.Element {
  // Hooks
  const { isActive, toggle } = useToggle(false)

  // Form
  const { control } = useFormContext<FormType>()
  const tax_inclusive = useWatch({
    control,
    name: "tax_inclusive"
  })

  return (
    <Controller
      control={control}
      name={`datajournal.${props.index}` as "datajournal.0"}
      render={({ field }) => {
        // Vars
        const value = field.value
        const amount = getAmount(value.amount, false, value.rate, tax_inclusive)

        if (isActive) {
          return (
            <DataJournal
              defaultValue={field.value}
              method="edit"
              onCancelEditing={toggle}
              onAddJournal={(value) => {
                field.onChange(value)
                toggle()
              }}
            />
          )
        } else {
          return (
            <tr>
              <td className="text-center">{props.index + 1}</td>
              <td>{field.value.coa_name}</td>
              <td>{field.value.memo}</td>
              <td className="text-right">{convertNumber(amount).intoCurrency}</td>
              <td>{field.value.project_name}</td>
              <td>{field.value.referensi}</td>
              <td>{field.value.tax_name}</td>

              <td>
                <section className="flex justify-center">
                  <ActionButton.Update onClick={toggle} />
                  <ActionButton.Delete onClick={props.onDeleteItem} />
                </section>
              </td>
            </tr>
          )
        }
      }}
    />
  )
}